// src/features/interview/InterviewPortal.jsx
import React from "react";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useInterviewFlow } from "./hooks/useInterviewFlow";
import TimerDisplay from "./components/TimerDisplay";
import AvatarDisplay from "./components/AvatarDisplay";
import RippleAnimationPanel from "./components/RippleAnimationPanel";
import CameraView from "./components/CameraView";
import AudioWaveform from "./components/AudioWaveform";
import AlertPanel from "./components/AlertPanel";
import QuestionDisplay from "./components/QuestionDisplay";
import InterviewCompleteScreen from "./components/InterviewCompleteScreen";
import UnifiedResumeScreen from "../../../components/shared/UnifiedResumeScreen";
import Loader from "../../../components/Loader";
import "./interview.css";
import host from "../../../global";
import { GIF_VARIANTS } from "./constants";
import { useTheme } from "../../../hooks/useTheme";
import ProctoringAlertModal from './components/ProctoringAlertModal';
import SecondaryCameraRecoveryGate from './components/SecondaryCameraRecoveryGate';
import { useScreenProctoring } from "../../../components/Instructions/hooks/useScreenProctoring";
import useVoiceActivity from "./hooks/useVoiceActivity";

const InterviewPortal = () => {
  const { interview_id } = useParams();
  const navigate = useNavigate();
  const {  theme } = useTheme();
  const fitContainerRef = useRef(null);
  const fitContentRef = useRef(null);

  useEffect(() => {
    const DESIGN_HEIGHT = 760; // px height at which the layout looks ideal; tune if needed
    const applyFit = () => {
      const container = fitContainerRef.current;
      const content = fitContentRef.current;
      if (!container || !content) return;
      const available = container.clientHeight - 32; // minus container p-4 (top+bottom)
      const z = Math.min(1, available / DESIGN_HEIGHT);
      content.style.height = `${DESIGN_HEIGHT}px`;
      content.style.zoom = z;
    };
    applyFit();
    const ro = new ResizeObserver(applyFit);
    if (fitContainerRef.current) ro.observe(fitContainerRef.current);
    window.addEventListener("resize", applyFit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyFit);
    };
  }, []);
  
  const isTabSwithingEnabled = useSelector((state)=>state.miscSlice.isTabSwitchingEnabled);
  // console.log({isTabSwithingEnabled})
  const {captureScreenshotBlob} = useScreenProctoring({
      checkInterval: 3000, // Check every 3 seconds
      autoStart: true,      // Auto-start monitoring
    });
  const { state, actions, refs, createConfetti, getActiveAlerts } = useInterviewFlow(interview_id, navigate);

  const {
      // State
      multipleMonitorsDetected,
      monitorCount,
      permissionsStatus:screenSharePermissionStatus,
      isScreenSharing,
      canShareScreen,

      // Functions
      requestScreenPermission,
      stopScreenSharing,
      checkForMultipleMonitors,
      validateScreenShareIntegrity
    } = useScreenProctoring({
      checkInterval: 3000, // Check every 3 seconds
      autoStart: true      // Auto-start monitoring
    });

  const {
    isPlaying,
    isRecording,
    currentQuestionIndex,
    currentQuestion,
    isNextButtonEnabled,
    isQuestionVisible,
    isSubmitLoading,
    isFinalizingUploads,
    uploadProgress,
    showResumeButton,
    isInterviewComplete,
    interviewNotFound,
    errormsg,
    isNextQuestionLoading,
    showTimer,
    timeLeft,
    isPlayingThankYou,
    currentGif,
    showResumeForMicCheck,
    stickyAlertCount,
    alertLog,
    isAlertPanelOpen,
    currentAlertIndex,
    showProctoringAlert,
    proctoringAlertMessage,
    showSecondaryCameraGate,
    useVoiceAnimation
  } = state;

  const isSpeaking = useVoiceActivity(isRecording, interview_id);

  const {
    handleStartInterview,
    handleTechnicalResumeComplete,
    handleNormalResumeComplete,
    handleTechnicalAcknowledge,
    stopRecordingAndProceed,
    handleAlertIconClick,
    navigateToNextAlert,
    navigateToPrevAlert,
    handleAlertClick,
    handleCameraReady,
    handleFaceViolation,
    handleMultipleFaceViolation,
    handleProhibitedObjectViolation,
    handleBodyPostureViolation,
    handleGazeViolation,
    handleStickyAlert,
    handleProctoringAcknowledge,
    handleFullscreenExitViolation,
    handleTabSwitchViolation,
    handleScreenShareStopedViolation,
    resumeInterview,
    handleSecondaryCameraRecovered,
    setIsIntentionalFullscreenChange
  } = actions;

  const { audioRef, proctoringVideoRef } = refs;

  const whitelabelEnabled = useSelector((state)=>state.miscSlice.whitelabelEnabled)
  


  // Render
  // console.log({isScreenSharing,screenSharePermissionStatus,canShareScreen});
  return (
    <div ref={fitContainerRef} className={`interview-no-select h-full flex items-center justify-center p-4  ${theme === 'light' && 'bg-[#FAF9F6]'}`}>
      {isRecording && showTimer && (
        <TimerDisplay timeLeft={timeLeft} showTimer={showTimer} />
      )}
      <div ref={fitContentRef} className="w-full rounded-3xl p-4 flex flex-col justify-between gap-5 h-full mt-2 mb-2">
        {interviewNotFound ? (
          <div className="flex flex-col items-center p-6 bg-gradient-to-r from-red-100 to-pink-200 border-l-4 border-red-500 rounded-xl shadow-xl max-w-lg mx-auto transform hover:scale-105 transition-transform duration-300 ease-in-out">
            <p className="text-red-800 text-center font-semibold tracking-wide">
              {errormsg}
            </p>
          </div>
        ) : isInterviewComplete ? (
          <InterviewCompleteScreen createConfetti={createConfetti} />
        ) : showResumeForMicCheck ? (
          <div className="relative min-h-[500px] w-full max-w-5xl mx-auto p-1">
            <div className={`relative h-full ${theme === 'light' ? 'bg-white shadow-sm border' : 'glass-effect backdrop-blur-xl shadow-xl '} rounded-xl p-8  transition-all duration-500 hover:shadow-sm`}>
               <UnifiedResumeScreen
                resumeType="technical"
                onResumeComplete={handleTechnicalResumeComplete}
                candidateName={state.candidateName}
                questionNumber={currentQuestionIndex + 1}
                interview_id={interview_id}
                host={host}
                whitelabelEnabled={theme === 'light'}
              />
            </div>
          </div>
        ) : showResumeButton ? (
          <div className="relative min-h-[500px] w-full max-w-5xl mx-auto p-1">
            <div className={`relative h-full ${theme === 'light' ? 'bg-white shadow-sm border' : 'glass-effect backdrop-blur-xl shadow-xl '} rounded-xl p-8  transition-all duration-500 hover:shadow-sm`}>
              <UnifiedResumeScreen
                resumeType="normal"
                onResumeComplete={handleNormalResumeComplete}
                candidateName={state.candidateName}
                questionNumber={currentQuestionIndex + 1}
                interview_id={interview_id}
                host={host}
                whitelabelEnabled={theme === 'light'}
              />
            </div>
          </div>
        ) : showSecondaryCameraGate ? (
          <div className="relative min-h-[500px] w-full max-w-5xl mx-auto p-1">
            <div className={`relative h-full ${theme === 'light' ? 'bg-white shadow-sm border' : 'glass-effect backdrop-blur-xl shadow-xl '} rounded-xl p-8  transition-all duration-500 hover:shadow-sm`}>
              <SecondaryCameraRecoveryGate
                interviewId={interview_id}
                onRecovered={handleSecondaryCameraRecovered}
                whitelabelEnabled={theme === 'light'}
              />
            </div>
          </div>
        ) : showProctoringAlert  ? (
          <div className="h-full flex items-center justify-center">
            <ProctoringAlertModal
              isOpen={true}
              message={proctoringAlertMessage}
              onAcknowledge={handleProctoringAcknowledge}
              candidateName={state.candidateName}
            />
          </div>
        ):
           (
          <>
            {useVoiceAnimation ? (
              /* ── Voice Animation Layout ── */
              <div className="flex flex-col gap-4 px-6 h-full">
                {/* Top row — voice animation (left) + camera (right) */}
                <div className="flex gap-4 flex-[8] min-h-0">
                  <div className="w-3/5 h-full">
                    <RippleAnimationPanel
                      isPlaying={isPlaying}
                      isRecording={isRecording}
                      isNextQuestionLoading={isNextQuestionLoading}
                      theme={theme}
                    />
                  </div>
                  <div className="w-2/5 h-full">
                    <CameraView
                      className="w-full h-full"
                      videoIncluded={state.videoIncluded}
                      proctoringVideoRef={proctoringVideoRef}
                      onViolation={handleCameraReady}
                      onCameraReady={(ready) => handleCameraReady(ready)}
                      isInterviewActive={state.isInterviewActive}
                      interviewId={interview_id}
                      onStickyAlert={handleStickyAlert}
                      onFaceViolation={handleFaceViolation}
                      onMultipleFaceViolation={handleMultipleFaceViolation}
                      onProhibitedObjectViolation={handleProhibitedObjectViolation}
                      onBodyPostureViolation={handleBodyPostureViolation}
                      onGazeViolation={handleGazeViolation}
                      onFullscreenChange={() => {}}
                      onTabSwitchViolation={handleTabSwitchViolation}
                      onFullscreenExitViolation={handleFullscreenExitViolation}
                      isRecording={isSpeaking && !isPlaying}
                    />
                  </div>
                </div>

                {/* Bottom — question full width */}
                <div className={`flex-[4] ${theme === 'light' ? 'bg-white shadow-sm border' : 'glass-effect backdrop-blur-xl shadow-sm'} rounded-2xl flex flex-col min-h-0`}>
                  <p className={`text-base font-medium tracking-widest px-6 pt-4 pb-1 flex-shrink-0 ${theme === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Question
                  </p>
                  <div className="flex-1 flex flex-col min-h-0">
                    <QuestionDisplay
                      state={{ ...state }}
                      actions={actions}
                      showStartButton={state.showStartButton}
                      captureScreenshotBlob={captureScreenshotBlob}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* ── Original Avatar Layout ──────────────── */
              <div className="flex flex-col gap-6 h-full">
                <div className="flex space-x-20 mx-16 justify-center">
                  <AvatarDisplay currentGif={currentGif} gifVariants={GIF_VARIANTS} isPlaying={isPlaying} isRecording={isRecording} />
                  <CameraView
                    videoIncluded={state.videoIncluded}
                    proctoringVideoRef={proctoringVideoRef}
                    onViolation={handleCameraReady}
                    onCameraReady={(ready) => handleCameraReady(ready)}
                    isInterviewActive={state.isInterviewActive}
                    interviewId={interview_id}
                    onStickyAlert={handleStickyAlert}
                    onFaceViolation={handleFaceViolation}
                    onMultipleFaceViolation={handleMultipleFaceViolation}
                    onProhibitedObjectViolation={handleProhibitedObjectViolation}
                    onBodyPostureViolation={handleBodyPostureViolation}
                    onGazeViolation={handleGazeViolation}
                    onFullscreenChange={(isFullscreen) => {
                      if (!isFullscreen && state.isInterviewActive) {
                        // open alert panel
                        // actions.setIsAlertPanelOpen?.(true);
                      } else if (isFullscreen) {
                        // clear alerts
                      }
                    }}
                    onTabSwitchViolation={handleTabSwitchViolation}
                    onFullscreenExitViolation={handleFullscreenExitViolation}
                  />
                </div>

                <AudioWaveform
                  isPlaying={isPlaying}
                  isRecording={isRecording}
                  isNextQuestionLoading={isNextQuestionLoading}
                  isWaitingForTechnicalAcknowledge={state.isWaitingForTechnicalAcknowledge}
                />

                <div className="flex-1 min-h-0">
                  <QuestionDisplay
                    state={{ ...state }}
                    actions={actions}
                    showStartButton={state.showStartButton}
                    captureScreenshotBlob={captureScreenshotBlob}
                  />
                </div>
              </div>
            )}

            {/* audio element attached for audioRef */}
            <audio ref={audioRef} />

            {isSubmitLoading && !isFinalizingUploads && <Loader />}

            {isFinalizingUploads && (
              <div
                className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center text-center px-6 ${
                  theme === "light" ? "bg-[#FAF9F6] text-gray-800" : "bg-gray-900 text-white"
                }`}
              >
                <h2 className="text-xl font-semibold mb-2">Saving your responses…</h2>
                <p className="text-sm opacity-80 mb-4">
                  Please don’t close this tab. We’re finishing the upload of your interview.
                </p>

                {uploadProgress.total > 0 && (
                  <>
                    <div className={`relative w-72 h-2 rounded-full overflow-hidden mb-2 ${theme === "light" ? "bg-gray-200" : "bg-gray-700"}`}>
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 overflow-hidden"
                        style={{
                          width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`,
                          background: theme === "light" ? "#6366f1" : "#818cf8",
                        }}
                      >
                        <div
                          className="absolute inset-y-0 w-24 animate-[shimmer_1.2s_ease-in-out_infinite]"
                          style={{
                            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                          }}
                        />
                      </div>
                    </div>
                    <p className="text-sm font-medium">
                      {Math.round((uploadProgress.done / uploadProgress.total) * 100)}% uploaded
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

       { (!isScreenSharing && isTabSwithingEnabled) && 
          <div className="h-full flex items-center justify-center">
            <ProctoringAlertModal
              isOpen={!isScreenSharing}
              message={"Please Resume Screen Sharing to Continue the Interview"}
              onAcknowledge={async ()=>{
                setIsIntentionalFullscreenChange(true);
                await requestScreenPermission().then(()=>{handleScreenShareStopedViolation()}).then(()=>{resumeInterview()});
              }}
              candidateName={state.candidateName}
            />
          </div>}
      {/* Alert Panel */}
      <AlertPanel
        getActiveAlerts={getActiveAlerts}
        alertLog={alertLog}
        stickyAlertCount={stickyAlertCount}
        isAlertPanelOpen={isAlertPanelOpen}
        handleAlertIconClick={handleAlertIconClick}
        currentAlertIndex={currentAlertIndex}
        navigateToPrevAlert={navigateToPrevAlert}
        navigateToNextAlert={navigateToNextAlert}
        handleAlertClick={handleAlertClick}
      />
    </div>
  );
};

export default InterviewPortal;
